import { FilterOptionsParams } from 'components/RevxJobListFilter';
import { Paged } from 'domains/appDomain';
import {
  Billing,
  BillingCategory,
  CostDistributionConfig,
  JobCostDistributionBannerDTO,
  Order,
  OrderHistoryItem,
  OrderHistorySearchGroupEnum,
  OrderItem,
  PaylodPlaceOrder,
  PaymentApiPayload,
  PurchaseOrder,
  PurchaseOrderJob,
  PurchaseWrapper,
} from 'domains/orderDomain';
import { ShippingReadDatePayload } from 'domains/shippingDomain';
import api from 'utils/api';

const orderService = {
  placeOrder: async (
    payload: PaylodPlaceOrder & { customerId: number | string }
  ) => {
    return api.post<OrderItem>('/api/orders/place', payload);
  },
  orderCheckout: async (payload: PaymentApiPayload) => {
    return api.post<string>('/api/orders/checkout', {
      ...payload,
      orgId: Number(localStorage.getItem('org')) || payload?.orgId,
    });
  },
  getHistory: async ({
    orgId,
    userId,
    page,
    size,
    jobOrOrderNumber,
    jobName,
    supplierName,
    requesterName,
    startOrderDate,
    endOrderDate,
    searchGroup,
    orderId,
  }: {
    userId: number;
    orgId: number;
    page: number;
    size: number;
    searchGroup: OrderHistorySearchGroupEnum;
    orderId?: string;
  } & FilterOptionsParams) => {
    let ret: Paged<OrderHistoryItem>;
    try {
      ret = await api.get<Paged<OrderHistoryItem>>(
        '/api/orders/history',
        {
          userId,
          orgId,
          size,
          page: page - 1 < 0 ? 0 : page - 1,
          sort: 'createAt,desc',
          jobOrOrderNumber,
          jobName,
          supplierName,
          requesterName,
          startOrderDate,
          endOrderDate,
          searchGroup,
          orderId,
        },
        undefined
      );
    } catch (e) {
      throw e;
    }
    return ret;
  },
  getHistoryById: async (orderId: string) => {
    return api.get<OrderHistoryItem>(`/api/orders/history/${orderId}`);
  },
  orderCancel: async (orderId: number | string) => {
    return api.put(`/api/orders/${orderId}/cancel`);
  },
  orderConfirm: async (payload: Partial<PurchaseOrder>) => {
    return api.post<Order>(`/api/orders`, {
      ...payload,
      orgId: Number(localStorage.getItem('org')) || payload?.orgId,
    });
  },
  getOrderDetail: async (payload: Partial<Order>) => {
    return api.get<Order>(`/api/orders/${payload.id}`);
  },
  orderUpdate: async (payload: Partial<OrderItem>) => {
    return api.post<OrderItem>(`/api/orders/${payload.id}/update`, {
      ...payload,
    });
  },
  getPurchaseJobList: (jobIds: number[]) => {
    return api.get<PurchaseOrderJob[]>(
      `/jobs/${jobIds.join(',')}/purchase-view`
    );
  },
  updateOrderPaymentMethod: async (
    orderId: number | string,
    paymentMethodId: number | string
  ) => {
    return api.post(`/payments/stripe/confirm-payment`, {
      orderId,
      paymentMethodId,
    });
  },
  confirmPurchase: (jobIds: number[]) => {
    return api.post<PurchaseWrapper>(
      `/jobs/${jobIds.join(',')}/confirm-to-purchase`
    );
  },
  orderSubmit: async (payload: Partial<Billing>) => {
    return api.post(`/orders/submit`, { ...payload });
  },
  batchShippingReadyDate: async (payload: ShippingReadDatePayload) => {
    return api.put<PurchaseOrderJob[]>(
      `/api/jobs/batch/shipping/ready-date`,
      payload
    );
  },
  listCategories: async () => {
    return api.get<BillingCategory[]>(
      '/jobs/cost/distribution/list/categories'
    );
  },
  listCostDistributionConfigs: async (categoryId: string) => {
    return api.get<CostDistributionConfig[]>(
      `/jobs/cost/distribution/list/configs/${categoryId}`
    );
  },
  queryBanners: async (site: string, siteDescription: string) => {
    return api.post<JobCostDistributionBannerDTO[]>(
      '/jobs/cost/distribution/query/banners',
      { site, siteDescription }
    );
  },
  importCostDistribution: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/jobs/cost/distribution/import', formData);
  },
  importBannerConfiguration: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/jobs/cost/distribution/banner-import', formData);
  },
  generateOracleString: async (payload: any) => {
    return api.post('/api/orders/generate-oracle-string', payload);
  },
};

export default orderService;
